import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

interface AuthState {
  session: Session | null;
  user: User | null;
  /**
   * True until the initial session lookup finishes. Routes must wait on this:
   * getSession() is async, so a signed-in user briefly looks signed-out on
   * every page load. Redirecting during that window bounces them to sign-in.
   */
  loading: boolean;
  sendMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // Restore any session already in storage (and, on a magic-link return,
    // the one supabaseClient's detectSessionInUrl just read out of the URL).
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    // Then track sign-in, sign-out, and token refreshes for the rest of the
    // session. Fires in every open tab, so they stay in sync.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return;
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Works for both localhost and the deployed origin without a rebuild.
        // Each origin must be listed under Authentication -> URL Configuration.
        emailRedirectTo: window.location.origin,
        // Sign-ups are invite-only, so a link request must never quietly
        // mint a new account. Set here too, not just in the dashboard, so
        // the behaviour is explicit at the call site.
        shouldCreateUser: false,
      },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      sendMagicLink,
      signOut,
    }),
    [session, loading, sendMagicLink, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an <AuthProvider>');
  return ctx;
}
